import { useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRaffles } from '../../api/queries';
import type { RaffleRecord } from '../../api/types';
import { Ic } from '../../components/Ic';
import { Empty } from '../../components/Empty';
import { usePresence } from '../../components/usePresence';
import { badgeColor, siteMeta } from '../../lib/siteMeta';
import { countdown, urgency, useNow } from '../../lib/countdown';
import { RaffleDetailModal } from '../Raffles/RaffleDetailModal';

const DAY = 86_400_000;

function startOfDay(ms: number) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

interface Column {
  key: string;
  label: string;
  sub: string;
  items: RaffleRecord[];
}

/** Seven day-columns from today plus "Later", keyed on the viewer's local
 *  calendar day of each raffle's cutoff. */
function buildColumns(raffles: RaffleRecord[], now: number): Column[] {
  const today = startOfDay(now);
  const cols: Column[] = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(today + i * DAY);
    return {
      key: String(i),
      label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : day.toLocaleDateString(undefined, { weekday: 'long' }),
      sub: day.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
      items: [],
    };
  });
  const later: Column = { key: 'later', label: 'Later', sub: 'beyond a week', items: [] };
  for (const r of raffles) {
    if (!r.cutoff) continue;
    const idx = Math.floor((startOfDay(r.cutoff * 1000) - today) / DAY);
    if (idx < 0) continue;
    (cols[idx] ?? later).items.push(r);
  }
  return later.items.length ? [...cols, later] : cols;
}

/** Vertical mouse-wheel over the week scrolls it sideways — the columns run
 *  left-to-right, so "scroll down" should mean "later". Only takes over while
 *  the grid can still move that way, so at either end the page scrolls as
 *  normal; native horizontal gestures, shift+wheel and ctrl-zoom pass through.
 *  A callback ref because the grid mounts only after data loads, and the
 *  listener must be non-passive to preventDefault. */
function useWheelToHorizontal() {
  const cleanup = useRef<() => void>();
  return useCallback((el: HTMLDivElement | null) => {
    cleanup.current?.();
    cleanup.current = undefined;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.shiftKey || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
      // Firefox reports line-mode deltas for mouse wheels.
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      const max = el.scrollWidth - el.clientWidth;
      if ((delta > 0 && el.scrollLeft >= max - 1) || (delta < 0 && el.scrollLeft <= 0)) return;
      e.preventDefault();
      el.scrollLeft += delta;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    cleanup.current = () => el.removeEventListener('wheel', onWheel);
  }, []);
}

function hasImage(url: string | null): url is string {
  return !!url && url !== 'N/A' && /^https?:/.test(url);
}

export function CalendarView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const now = useNow(30_000);
  const { data, error, isLoading } = useRaffles({
    status: 'posted',
    active: true,
    sort: 'closes',
    order: 'asc',
    limit: 200,
  });
  const raffles = data?.items ?? [];
  const columns = buildColumns(raffles, now);

  const selected = id ? raffles.find((r) => String(r.id) === id) : undefined;
  const detail = usePresence(selected ? { raffle: selected } : undefined);
  const gridRef = useWheelToHorizontal();

  return (
    <div className="view-pad cal">
      <header className="page-head">
        <div>
          <h1>Calendar</h1>
          <p className="page-sub">Open raffles by the day entries close</p>
        </div>
      </header>

      {error ? (
        <Empty icon={Ic.alert} title="Could not load raffles" detail={error instanceof Error ? error.message : String(error)} />
      ) : isLoading ? (
        <div className="muted loading-delayed">Loading…</div>
      ) : (
        <div className="cal-grid" ref={gridRef}>
          {columns.map((col) => (
            <section key={col.key} className={`cal-col ${col.key === '0' ? 'is-today' : ''}`}>
              <header className="cal-col-head">
                <span className="cal-day">{col.label}</span>
                <span className="cal-date">{col.sub}</span>
                <span className="cal-n num">{col.items.length || ''}</span>
              </header>
              {col.items.length === 0 ? (
                <div className="cal-empty">—</div>
              ) : (
                col.items.map((r) => (
                  <button key={r.id} className={`cal-item u-${urgency(r.cutoff, now)}`} onClick={() => navigate(`/calendar/${r.id}`)}>
                    <span className="cal-thumb">
                      {hasImage(r.picture_url) ? (
                        <img src={r.picture_url} alt="" loading="lazy" />
                      ) : (
                        <span style={{ background: badgeColor(r.site) }}>{siteMeta(r.site).short}</span>
                      )}
                    </span>
                    <span className="cal-body">
                      <span className="cal-time num">
                        {new Date((r.cutoff ?? 0) * 1000).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                        <span className="cal-left">{countdown(r.cutoff, now)}</span>
                      </span>
                      <span className="cal-title">{r.product ?? 'Untitled raffle'}</span>
                      <span className="cal-site">{r.site}</span>
                    </span>
                  </button>
                ))
              )}
            </section>
          ))}
        </div>
      )}

      {detail.item && (
        <RaffleDetailModal {...detail.item} open={detail.open} onExited={detail.onExited} closeTo="/calendar" />
      )}
    </div>
  );
}
